import React, {useMemo} from "react";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import {formatDateDE} from "../Statistics/format.js";
import {formatDuration, formatTimeDE} from "./format.js";
import NutzungWidget from "./NutzungWidget.jsx";

const TAGESBEGINN_STUNDE = 6;
const TAGESENDE_STUNDE = 22;
const TAGESSPANNE_STUNDEN = TAGESENDE_STUNDE - TAGESBEGINN_STUNDE;

/** Europe/Berlin statt Browser-Zeitzone - sonst rutscht eine Sitzung kurz
 * nach Mitternacht in den falschen Tag der Leiste. */
function berlinTagISO(iso) {
    return new Intl.DateTimeFormat("en-CA", {timeZone: "Europe/Berlin"}).format(new Date(iso));
}

function berlinStundenanteil(iso) {
    const teile = new Intl.DateTimeFormat("de-DE", {
        timeZone: "Europe/Berlin",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).formatToParts(new Date(iso));
    const stunde = Number(teile.find((teil) => teil.type === "hour")?.value || 0);
    const minute = Number(teile.find((teil) => teil.type === "minute")?.value || 0);
    return stunde + minute / 60;
}

/**
 * Sitzungen als Tagesleiste: je Tag eine Zeile, je Sitzung ein Balken von
 * Anmeldung bis letzter Aktivität, positioniert nach Uhrzeit (Europe/
 * Berlin). Zeitraum der Leiste 6-22 Uhr - deckt den gewöhnlichen Arbeitstag
 * ab, ohne dass ein einzelner Nachtzugriff die ganze Skala staucht.
 *
 * Angenommene Feldnamen je Sitzung entsprechen der Tabelle
 * activity_sessions aus der Spezifikation (Abschnitt 4): started_at,
 * ended_at (bzw. ersatzweise last_seen_at, falls die Sitzung noch läuft),
 * active_seconds, uuid.
 */
export default function NutzungProfilSitzungen({sitzungen, loading, error}) {
    const rows = Array.isArray(sitzungen) ? sitzungen : [];

    const nachTag = useMemo(() => {
        const map = new Map();
        rows.forEach((sitzung) => {
            if (!sitzung.started_at) {
                return;
            }
            const tag = berlinTagISO(sitzung.started_at);
            if (!map.has(tag)) {
                map.set(tag, []);
            }
            map.get(tag).push(sitzung);
        });
        return [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
    }, [rows]);

    return (
        <NutzungWidget
            title="Sitzungen"
            help="Jede Zeile ein Tag, jeder Balken eine Sitzung - positioniert nach Uhrzeit (6 bis 22 Uhr, Europe/Berlin)."
            loading={loading}
            error={error}
            empty={!loading && !error && nachTag.length === 0}
            height={320}
        >
            <div style={{maxHeight: 320, overflowY: "auto"}}>
                {nachTag.map(([tag, sitzungenAmTag]) => (
                    <div key={tag} className="d-flex align-items-center gap-2 py-1 border-bottom">
                        <div className="small text-muted flex-shrink-0" style={{width: 88}}>{formatDateDE(tag)}</div>
                        <div className="position-relative flex-grow-1" style={{height: 20}}>
                            {sitzungenAmTag.map((sitzung) => {
                                const startAnteil = Math.min(
                                    Math.max(berlinStundenanteil(sitzung.started_at) - TAGESBEGINN_STUNDE, 0),
                                    TAGESSPANNE_STUNDEN
                                );
                                const endeIso = sitzung.ended_at || sitzung.last_seen_at || sitzung.started_at;
                                const endeAnteil = Math.min(
                                    Math.max(berlinStundenanteil(endeIso) - TAGESBEGINN_STUNDE, startAnteil + 0.05),
                                    TAGESSPANNE_STUNDEN
                                );
                                const links = (startAnteil / TAGESSPANNE_STUNDEN) * 100;
                                const breite = Math.max(((endeAnteil - startAnteil) / TAGESSPANNE_STUNDEN) * 100, 0.6);
                                return (
                                    <OverlayTrigger
                                        key={sitzung.uuid || `${tag}-${sitzung.started_at}`}
                                        placement="top"
                                        overlay={
                                            <Tooltip>
                                                {formatTimeDE(sitzung.started_at)}–{formatTimeDE(endeIso)} Uhr, aktiv {formatDuration(sitzung.active_seconds)}
                                            </Tooltip>
                                        }
                                    >
                                        <div
                                            className="position-absolute rounded-1"
                                            style={{
                                                left: `${links}%`,
                                                width: `${breite}%`,
                                                top: 2,
                                                bottom: 2,
                                                backgroundColor: "#2f6fd0",
                                                opacity: 0.75,
                                            }}
                                        />
                                    </OverlayTrigger>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </NutzungWidget>
    );
}
